import { Dispatch } from 'redux';
import { flatMap, map, some, isEmpty, debounce } from 'lodash';
import { ReduxStore } from '../payload-types';
import {
  QuoteResponse,
  QUOTE_INDIVIDUAL_REQUEST_FAILED,
  QUOTE_INDIVIDUAL_REQUEST_START,
  QUOTE_INDIVIDUAL_REQUEST_SUCCESS,
  CALCULATE_INDIVIDUAL_REQUEST_START,
  QUOTE_REQUEST_FAILED,
  QUOTE_REQUEST_START,
  QUOTE_REQUEST_SUCCESS,
  COVERME_CLEAR_QUOTEDATA
} from './types';
import { ApiClient } from '../api.client';
import { LINKS, FEATURE_FLAGS } from '@trip-ux/constants';
import { prepareQuote, prepareQuoteForPolicyInfo } from '../request.mappers';
import { getCovermeFlag } from '../coverme-flags';
import { checkFeatureFlag } from '../utils';

const EMPTY_PLANS_ERROR = 'Plans not available from recommendation';

// When the `destination_pricing` feature flag is on for the current user,
// route the quote call to quote-api v2 (strict validation + destination
// pricing). Same URL — v2 is selected via the `api-version: 2` header.
const getQuoteRequestConfig = (state: ReduxStore) =>
  checkFeatureFlag(state.userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING)
    ? { headers: { 'api-version': '2' } }
    : undefined;

// R13 — Debounce rapid consecutive quote actions.
// On plan-selection, users often adjust trip cost + rider + plan choice within
// 1-2 s. Each change dispatches one of the 4 quote actions (getQuote,
// getQuoteForPolicyInfo, getIndividualQuote, calculateIndividualQuote), each
// POSTing to /api/quote. The `isAnyLoading` guard prevents concurrent calls
// but does not coalesce rapid sequentials. Real-user data shows 6 K+ quote
// calls/day on plan-selection alone. See RCA § 1.9.
//
// When the runtime flag `r13QuoteDebounce` is on, rapid dispatches share a
// single 150 ms trailing-edge debounce timer. Only the LAST scheduled work
// function runs; all earlier dispatchers receive a Promise that resolves
// when that final work completes. Semantics: user's LAST intent within the
// burst is what fires (state read inside the work function reflects the
// latest state at fire time, so trip cost / rider / plan choice are always
// the freshest).
//
// Flag is sourced from `window.__COVERME_FLAGS__.r13QuoteDebounce` at
// runtime (server-injected via booking.controller.ts render context) with
// a `process.env.COVERME_R13_QUOTE_DEBOUNCE` fallback for Node/SSR and
// unit tests. See libs/redux-store/src/coverme-flags.ts. Default off.
// Flip per environment via chart values.
const QUOTE_DEBOUNCE_MS = 150;

let pendingResolvers: Array<() => void> = [];
let latestWork: (() => Promise<void>) | null = null;
let flushDebounced: ReturnType<typeof debounce> | null = null;

const buildFlusher = () =>
  debounce(() => {
    const work = latestWork;
    const resolvers = pendingResolvers;
    latestWork = null;
    pendingResolvers = [];
    if (!work) {
      resolvers.forEach((r) => r());
      return;
    }
    work().finally(() => resolvers.forEach((r) => r()));
  }, QUOTE_DEBOUNCE_MS);

const withQuoteDebounce = (work: () => Promise<void>): Promise<void> => {
  if (!getCovermeFlag('r13QuoteDebounce')) {
    return work();
  }
  if (!flushDebounced) {
    flushDebounced = buildFlusher();
  }
  const flusher = flushDebounced;
  latestWork = work;
  return new Promise<void>((resolve) => {
    pendingResolvers.push(resolve);
    flusher();
  });
};

// Test-only reset. Not exported through the barrel; imported directly by
// actions.spec.ts to clear debounce state between test cases so fake-timer
// setups do not leak across tests.
export const resetQuoteDebounceForTests = (): void => {
  latestWork = null;
  pendingResolvers = [];
  flushDebounced?.cancel();
  flushDebounced = null;
};

const handleQuoteError = (dispatch: Dispatch, payload, error): Promise<void> => {
  dispatch({
    type: QUOTE_REQUEST_FAILED,
    payload: map(payload.paxes, pax => ({
      error,
      loading: false,
      referenceNumber: pax.referenceNumber
    }))
  });
  return Promise.resolve();
};

export const getQuote =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const doWork = (): Promise<void> => {
      const { recommendation } = getState();
      const isAnyLoading = Object.values(recommendation).some(r => typeof r === 'object' && r !== null && (r as { loading?: boolean }).loading);
      if (isAnyLoading) {
        return Promise.resolve();
      }
      const payload = prepareQuote(getState());
      if (some(payload.paxes, pax => isEmpty(pax.plans))) {
        console.info('[getQuote] Blocked — one or more paxes have empty plans');
        return handleQuoteError(dispatch, payload, new Error(EMPTY_PLANS_ERROR));
      }
      dispatch({
        type: QUOTE_REQUEST_START,
        payload: flatMap(payload.paxes, 'referenceNumber')
      });
      return apiClient(dispatch, getState)
        .post<QuoteResponse>(LINKS.QUOTE, payload, getQuoteRequestConfig(getState()))
        .then(data => {
          dispatch({ type: QUOTE_REQUEST_SUCCESS, payload: data });
        })
        .catch(e => handleQuoteError(dispatch, payload, e));
    };
    return withQuoteDebounce(doWork);
  };

export const getQuoteForPolicyInfo =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const doWork = (): Promise<void> => {
      const { recommendation } = getState();
      const isAnyLoading = Object.values(recommendation).some(r => typeof r === 'object' && r !== null && (r as { loading?: boolean }).loading);
      if (isAnyLoading) {
        return Promise.resolve();
      }
      const payload = prepareQuoteForPolicyInfo(getState());
      if (some(payload.paxes, pax => isEmpty(pax.plans))) {
        console.info('[getQuoteForPolicyInfo] Blocked — one or more paxes have empty plans');
        return handleQuoteError(dispatch, payload, new Error(EMPTY_PLANS_ERROR));
      }
      dispatch({
        type: QUOTE_REQUEST_START,
        payload: flatMap(payload.paxes, 'referenceNumber')
      });
      return apiClient(dispatch, getState)
        .post<QuoteResponse>(LINKS.QUOTE, payload, getQuoteRequestConfig(getState()))
        .then(data => {
          dispatch({ type: QUOTE_REQUEST_SUCCESS, payload: data });
        })
        .catch(e => handleQuoteError(dispatch, payload, e));
    };
    return withQuoteDebounce(doWork);
  };

/**
 *  TO DO: To be synced with BE for optimization
 *  Possible idea: add a new field for companion count to be used in re-quoting for individual tripParams
 **/
export const getIndividualQuote =
  (paxId: number) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const doWork = (): Promise<void> => {
      const { recommendation } = getState();
      const isAnyLoading = Object.values(recommendation).some(r => typeof r === 'object' && r !== null && (r as { loading?: boolean }).loading);
      if (isAnyLoading) {
        return Promise.resolve();
      }
      dispatch({
        type: QUOTE_INDIVIDUAL_REQUEST_START,
        payload: paxId
      });
      const payload = prepareQuote(getState());
      if (some(payload.paxes, pax => isEmpty(pax.plans))) {
        console.info('[getIndividualQuote] Blocked — one or more paxes have empty plans');
        dispatch({
          type: QUOTE_INDIVIDUAL_REQUEST_FAILED,
          payload: { referenceNumber: paxId, loading: false, error: new Error(EMPTY_PLANS_ERROR) }
        });
        return Promise.resolve();
      }
      return apiClient(dispatch, getState)
        .post<QuoteResponse>(LINKS.QUOTE, payload, getQuoteRequestConfig(getState()))
        .then(quoteData => {
          dispatch({
            type: QUOTE_INDIVIDUAL_REQUEST_SUCCESS,
            payload: { referenceNumber: paxId, quoteData, loading: false }
          });
        })
        .catch(e => {
          dispatch({
            type: QUOTE_INDIVIDUAL_REQUEST_FAILED,
            payload: { referenceNumber: paxId, loading: false, error: e }
          });
        });
    };
    return withQuoteDebounce(doWork);
  };
export const calculateIndividualQuote =
  (paxId: number) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const doWork = (): Promise<void> => {
      const { recommendation } = getState();
      const isAnyLoading = Object.values(recommendation).some(r => typeof r === 'object' && r !== null && (r as { loading?: boolean }).loading);
      if (isAnyLoading) {
        return Promise.resolve();
      }
      dispatch({
        type: CALCULATE_INDIVIDUAL_REQUEST_START,
        payload: paxId
      });
      const payload = prepareQuote(getState());
      if (some(payload.paxes, pax => isEmpty(pax.plans))) {
        console.info('[calculateIndividualQuote] Blocked — one or more paxes have empty plans');
        return handleQuoteError(dispatch, payload, new Error(EMPTY_PLANS_ERROR));
      }
      return apiClient(dispatch, getState)
        .post<QuoteResponse>(LINKS.QUOTE, payload, getQuoteRequestConfig(getState()))
        .then(quoteData => {
          dispatch({
            type: QUOTE_REQUEST_SUCCESS,
            payload: quoteData
          });
        })
        .catch(e => handleQuoteError(dispatch, payload, e));
    };
    return withQuoteDebounce(doWork);
  };

export const coverMeClearQuoteData =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: COVERME_CLEAR_QUOTEDATA
    });
  };
